import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LoginRequest, QueryKey } from 'api/model';
import { getMe, loginRequest, logoutRequest } from 'api/auth/requests';
import { SessionExpiredError } from 'api/auth/session';
import { clearTokens, getRefreshToken, getTokens, onSessionEnd, setTokens } from 'api/auth/tokenStore';
import { AuthContext, AuthContextValue, AuthStatus } from './authContext';

const fetchCurrentUser = () => {
  if (!getTokens()) {
    return Promise.reject(new SessionExpiredError('No stored session'));
  }
  return getMe();
};

type Props = {
  children: ReactNode;
};

const AuthProvider = ({ children }: Props) => {
  const queryClient = useQueryClient();
  const [isSignedOut, setIsSignedOut] = useState(false);

  const {
    data: user,
    isPending,
    refetch,
  } = useQuery({
    queryKey: [QueryKey.CurrentUser],
    queryFn: fetchCurrentUser,
    retry: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  useEffect(() => onSessionEnd(() => setIsSignedOut(true)), []);

  const login = useCallback(
    async (credentials: LoginRequest) => {
      const pair = await loginRequest(credentials);
      setTokens(pair);
      queryClient.clear();
      setIsSignedOut(false);

      const { error } = await refetch();
      if (error) {
        clearTokens();
        throw error;
      }
    },
    [queryClient, refetch],
  );

  const logout = useCallback(async () => {
    const refreshToken = getRefreshToken();
    clearTokens();
    setIsSignedOut(true);
    queryClient.removeQueries({ queryKey: [QueryKey.CurrentUser] });

    try {
      if (refreshToken) await logoutRequest(refreshToken);
    } catch (error) {
      console.warn('Logout request failed, the local session was cleared anyway', error);
    }
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(() => {
    const currentUser = isSignedOut ? null : (user ?? null);
    const status: AuthStatus = currentUser
      ? 'authenticated'
      : isPending && !isSignedOut
        ? 'loading'
        : 'unauthenticated';

    return {
      status,
      user: currentUser,
      isSuperuser: currentUser?.is_superuser ?? false,
      login,
      logout,
    };
  }, [user, isPending, isSignedOut, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
